import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Heart,
  Shield,
  Eye,
  EyeOff,
  Calendar,
  Settings,
  User,
  Crown,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { getWebIDsByUser } from '../../services/webids';
import { WebIDData } from '../../services/webids';
import { Button } from '../ui';
import { CopyText } from '../ui/CopyButton';
import LoadingSpinner from '../ui/LoadingSpinner';

/**
 * @component AdminUserDashboard
 * @description Dashboard para que los administradores vean los perfiles de emergencia de un usuario específico
 */
const AdminUserDashboard: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [emergencyProfiles, setEmergencyProfiles] = useState<WebIDData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Verificar que el usuario actual es administrador
  useEffect(() => {
    if (user && !user.email?.includes('@nfcores.cl')) {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  // Cargar perfiles de emergencia del usuario
  useEffect(() => {
    const loadUserProfiles = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        setError(null);
        
        const profiles = await getWebIDsByUser(userId);
        setEmergencyProfiles(profiles);
      } catch (err) {
        console.error('Error cargando perfiles del usuario:', err);
        setError(err instanceof Error ? err.message : 'Error cargando los perfiles');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfiles();
  }, [userId]);

  const handleRefresh = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const profiles = await getWebIDsByUser(userId);
      setEmergencyProfiles(profiles);
    } catch (err) {
      console.error('Error refrescando perfiles:', err);
      setError(err instanceof Error ? err.message : 'Error refrescando los perfiles');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/admin/webids')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <Button onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/webids')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a WebIDs
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizar
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Crown className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard del Usuario</h1>
              <p className="text-gray-600">
                Vista administrativa de los perfiles de emergencia del usuario: {userId}
              </p>
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Información del Usuario</h2>
                <div className="flex items-center gap-4 mt-1">
                  <CopyText
                    text={userId || ''}
                    displayText={`ID: ${userId}`}
                    textClassName="text-sm text-gray-500"
                    buttonVariant="ghost"
                  />
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">Usuario Verificado</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-500">Total de Perfiles</div>
              <div className="text-2xl font-bold text-gray-900">{emergencyProfiles.length}</div>
            </div>
          </div>
        </div>

        {/* Emergency Profiles List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Perfiles de Emergencia del Usuario
            </h2>
          </div>

          {emergencyProfiles.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No tiene perfiles de emergencia
              </h3>
              <p className="text-gray-600">
                Este usuario aún no ha activado ningún perfil de emergencia
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {emergencyProfiles.map((profile) => (
                <div key={profile.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Heart className="h-6 w-6 text-primary-600" />
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {(profile.emergencyProfile as any)?.fullName || (profile.emergencyProfile as any)?.name || 'Sin nombre'}
                        </h3>
                        <div className="flex items-center gap-4 mt-1">
                          <CopyText
                            text={profile.id}
                            displayText={`WebID: ${profile.id}`}
                            textClassName="text-sm text-gray-500"
                            buttonVariant="ghost"
                          />
                          
                          <div className="flex items-center gap-2">
                            {profile.emergencyProfile?.isPublic ? (
                              <>
                                <Eye className="h-4 w-4 text-green-600" />
                                <span className="text-sm text-green-600">Público</span>
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-500">Privado</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          profile.activationStatus === 'onboarding_completed'
                            ? 'bg-green-100 text-green-800'
                            : profile.activationStatus === 'activated'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {profile.activationStatus === 'onboarding_completed'
                            ? 'Completado'
                            : profile.activationStatus === 'activated'
                            ? 'Pendiente Configuración'
                            : 'No Activado'
                          }
                        </div>
                        
                        {profile.onboardingCompletedAt && (
                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(profile.onboardingCompletedAt.toDate()).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/w/${profile.id}`, '_blank')}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          Ver Perfil Público
                        </Button>
                        
                        {profile.activationStatus === 'onboarding_completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/profile/${profile.id}/edit`)}
                            className="flex items-center gap-1"
                          >
                            <Settings className="h-3 w-3" />
                            Editar como Admin
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserDashboard;
